import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { testimonials } from '../mockData';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  // TODO: Replace '#' with the real Google Business Profile review URL once available.
  const googleReviewsUrl = '#';

  return (
    <section id="testimonials" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Parent Testimonials
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            What Parents Say About Us
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Real stories from families who have experienced 
            the Read Right Academy difference.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-5xl mx-auto">
          {/* Main Testimonial Image Display */}
          <div className="relative aspect-square max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-white">
            <img
              src={testimonials[currentIndex].image}
              alt={testimonials[currentIndex].name}
              className="w-full h-full object-contain bg-white"
            />
            {/* White overlay to hide the Read Right Academy logo at the bottom of each testimonial graphic */}
            <div
              aria-hidden="true"
              className="absolute bottom-0 left-0 right-0 h-[12%] bg-white pointer-events-none"
            />
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevTestimonial}
            data-testid="testimonial-prev-btn"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 bg-white hover:bg-[#3B4BA0] text-gray-800 hover:text-white p-3 rounded-full shadow-lg transition-all duration-300 z-10 group"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={nextTestimonial}
            data-testid="testimonial-next-btn"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 bg-white hover:bg-[#3B4BA0] text-gray-800 hover:text-white p-3 rounded-full shadow-lg transition-all duration-300 z-10 group"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center space-x-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-12 h-3 bg-[#3B4BA0]'
                    : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Testimonial Text Below Image */}
          <div className="mt-8 text-center max-w-3xl mx-auto">
            <p className="text-gray-700 text-lg leading-relaxed mb-4 italic">
              "{testimonials[currentIndex].content}"
            </p>
            <p className="text-[#3B4BA0] font-semibold text-lg">
              — {testimonials[currentIndex].name}
            </p>
          </div>
        </div>

        {/* Google Reviews CTA */}
        <div className="mt-14 flex justify-center">
          <a
            href={googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-btn"
            className="inline-flex items-center gap-3 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-[#3B4BA0] px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all duration-300 group"
          >
            <span className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ))}
            </span>
            <span className="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-[#3B4BA0] transition-colors">
              Read Reviews on Google
            </span>
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
          </a>
        </div>

        {/* Trust Banner */}
        <div className="mt-10 text-center">
          <div className="inline-flex flex-wrap justify-center items-center gap-8 bg-gradient-to-r from-blue-50 to-teal-50 px-8 py-6 rounded-2xl">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#3B4BA0]">20+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div className="h-12 w-px bg-gray-300 hidden sm:block" />
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-[#3B4BA0] leading-tight">Trusted</div>
              <div className="text-sm text-gray-600">by Families Worldwide</div>
            </div>
            <div className="h-12 w-px bg-gray-300 hidden sm:block" />
            <div className="text-center">
              <div className="text-xl font-bold text-[#3B4BA0] leading-tight">Positive</div>
              <div className="text-sm text-gray-600">Parent Feedback</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;